import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';

interface VehicleData {
  plateNumber: string;
  plateState: string;
  make: string;
  model: string;
  year: number | null;
  color: string;
  vin: string;
}

interface CitationVehicleProps {
  value: any;
  npsdVehicleId?: number | null;
  isReadOnly?: boolean;
  isLocked?: boolean;
  onInput?: (data: VehicleData) => void;
  onChange?: (data: VehicleData) => void;
  onLinkVehicle?: () => void;
  onCreateVehicle?: (data: VehicleData) => void;
  onUnlinkVehicle?: () => void;
  onShowLinkedVehicle?: () => void;
  onClear?: () => void;
}

function createEmptyData(): VehicleData {
  return {
    plateNumber: '',
    plateState: '',
    make: '',
    model: '',
    year: null,
    color: '',
    vin: '',
  };
}

function parseValueToData(val: any): VehicleData {
  if (!val) return createEmptyData();
  return {
    plateNumber: val.plateNumber || '',
    plateState: val.plateState || val.plateStateValue || '',
    make: val.make || val.makeValue || '',
    model: val.model || val.modelValue || '',
    year: val.year || null,
    color: val.color || val.colorValue || '',
    vin: val.vin || '',
  };
}

export function CitationVehicle({
  value,
  npsdVehicleId,
  isReadOnly = false,
  isLocked = false,
  onInput,
  onChange,
  onLinkVehicle,
  onCreateVehicle,
  onUnlinkVehicle,
  onShowLinkedVehicle,
  onClear,
}: CitationVehicleProps) {
  const { theme } = useTheme();
  const plateInputRef = useRef<HTMLInputElement>(null);

  const [localData, setLocalData] = useState<VehicleData>(() => parseValueToData(value));
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);

  // Track whether we are emitting to prevent circular updates
  const isEmittingRef = useRef(false);

  // Sync localData when value prop changes
  useEffect(() => {
    if (isEmittingRef.current) return;
    setLocalData(parseValueToData(value));
  }, [value]);

  // Derived state
  const isLinked = useMemo(
    () => npsdVehicleId !== null && npsdVehicleId !== undefined,
    [npsdVehicleId]
  );

  const isFieldsDisabled = useMemo(
    () => isReadOnly || isLocked || isLinked,
    [isReadOnly, isLocked, isLinked]
  );

  const canCreate = useMemo(
    () =>
      !isReadOnly &&
      !isLocked &&
      !isLinked &&
      !!(localData.plateNumber || localData.vin),
    [isReadOnly, isLocked, isLinked, localData.plateNumber, localData.vin]
  );

  const hasAnyData = useMemo(
    () =>
      !!(
        localData.plateNumber ||
        localData.plateState ||
        localData.make ||
        localData.model ||
        localData.year ||
        localData.color ||
        localData.vin ||
        isLinked
      ),
    [localData, isLinked]
  );

  const showEmptyState = useMemo(
    () => !hasAnyData && !isReadOnly && !isLocked && !isManualEntry,
    [hasAnyData, isReadOnly, isLocked, isManualEntry]
  );

  // Emit changes to parent
  const emitChange = useCallback(
    (updatedData: VehicleData) => {
      isEmittingRef.current = true;
      const dataCopy = { ...updatedData };
      onInput?.(dataCopy);
      onChange?.(dataCopy);
      // Reset the emitting flag after a microtask to allow React to process
      Promise.resolve().then(() => {
        isEmittingRef.current = false;
      });
    },
    [onInput, onChange]
  );

  // Field change handler
  const handleFieldChange = useCallback(
    (field: keyof VehicleData, rawValue: string) => {
      setLocalData((prev) => {
        let parsedValue: string | number | null = rawValue;
        if (field === 'year') {
          parsedValue = rawValue === '' ? null : Number(rawValue);
        }
        const updated = { ...prev, [field]: parsedValue };
        // Schedule emit after state update
        Promise.resolve().then(() => emitChange(updated));
        return updated;
      });
    },
    [emitChange]
  );

  // Actions
  const handleLinkVehicle = useCallback(() => {
    if (isReadOnly || isLocked) return;
    onLinkVehicle?.();
  }, [isReadOnly, isLocked, onLinkVehicle]);

  const handleCreateVehicle = useCallback(() => {
    if (isReadOnly || isLocked) return;
    if (!localData.plateNumber && !localData.vin) {
      if (typeof (window as any).App?.showPopupNotification === 'function') {
        (window as any).App.showPopupNotification(
          'Plate number or VIN is required to create a vehicle.',
          'error'
        );
      }
      return;
    }
    onCreateVehicle?.({ ...localData });
  }, [isReadOnly, isLocked, localData, onCreateVehicle]);

  const handleUnlinkVehicle = useCallback(() => {
    if (isReadOnly || isLocked) return;
    onUnlinkVehicle?.();
  }, [isReadOnly, isLocked, onUnlinkVehicle]);

  const handleShowLinkedVehicle = useCallback(() => {
    if (isLinked) {
      onShowLinkedVehicle?.();
    }
  }, [isLinked, onShowLinkedVehicle]);

  const handleOpenClearConfirm = useCallback(() => {
    if (isReadOnly || isLocked) return;
    setShowClearConfirm(true);
  }, [isReadOnly, isLocked]);

  const handleConfirmClear = useCallback(() => {
    setShowClearConfirm(false);
    setLocalData(createEmptyData());
    setIsManualEntry(false);
    onClear?.();
  }, [onClear]);

  const handleCancelClear = useCallback(() => {
    setShowClearConfirm(false);
  }, []);

  const startEnteringData = useCallback(() => {
    setIsManualEntry(true);
  }, []);

  // Focus plate number input when entering manual entry mode
  useEffect(() => {
    if (isManualEntry && plateInputRef.current) {
      plateInputRef.current.focus();
    }
  }, [isManualEntry]);

  // Build class names for wrapper
  const wrapperClassName = [
    'citation-vehicle',
    theme,
    hasAnyData ? 'vehicle-has-data' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClassName}>
      {/* Empty State - Compact add vehicle prompt when no data */}
      {showEmptyState ? (
        <div className="vehicle-empty-state">
          <div className="empty-state-icon">
            <i className="fas fa-car"></i>
          </div>
          <div className="empty-state-text">
            No vehicle information added. Vehicle is optional.
          </div>
          <div className="empty-state-actions">
            <button
              type="button"
              className="btn btn-link-action"
              onClick={handleLinkVehicle}
              title="Link to existing vehicle"
            >
              <i className="fas fa-link"></i> Link Existing
            </button>
            <button
              type="button"
              className="btn btn-link-action"
              onClick={startEnteringData}
              title="Enter vehicle details manually"
            >
              <i className="fas fa-edit"></i> Enter Details
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Vehicle Form - shown when there's data or when entering manually */}
          <div className="vehicle-grid">
            {/* Row 1: Plate Number + State grouped */}
            <div className="vehicle-row vehicle-row--plate">
              <div className="vehicle-field">
                <label>Plate Number</label>
                <input
                  ref={plateInputRef}
                  type="text"
                  className="form-input"
                  value={localData.plateNumber}
                  disabled={isFieldsDisabled}
                  onChange={(e) => handleFieldChange('plateNumber', e.target.value)}
                  placeholder="Plate number"
                />
              </div>
              <div className="vehicle-field vehicle-field--small">
                <label>State</label>
                <input
                  type="text"
                  className="form-input"
                  value={localData.plateState}
                  disabled={isFieldsDisabled}
                  onChange={(e) => handleFieldChange('plateState', e.target.value)}
                  placeholder="ST"
                  maxLength={2}
                />
              </div>
            </div>

            {/* Row 2: VIN (full width on mobile) */}
            <div className="vehicle-row">
              <div className="vehicle-field vehicle-field--wide">
                <label>VIN</label>
                <input
                  type="text"
                  className="form-input"
                  value={localData.vin}
                  disabled={isFieldsDisabled}
                  onChange={(e) => handleFieldChange('vin', e.target.value)}
                  placeholder="Vehicle Identification Number"
                  maxLength={17}
                />
              </div>
            </div>

            {/* Row 3: Year + Make grouped */}
            <div className="vehicle-row vehicle-row--details">
              <div className="vehicle-field vehicle-field--small">
                <label>Year</label>
                <input
                  type="number"
                  className="form-input"
                  value={localData.year ?? ''}
                  disabled={isFieldsDisabled}
                  onChange={(e) => handleFieldChange('year', e.target.value)}
                  placeholder="Year"
                  min={1900}
                  max={2100}
                />
              </div>
              <div className="vehicle-field">
                <label>Make</label>
                <input
                  type="text"
                  className="form-input"
                  value={localData.make}
                  disabled={isFieldsDisabled}
                  onChange={(e) => handleFieldChange('make', e.target.value)}
                  placeholder="Make"
                />
              </div>
            </div>

            {/* Row 4: Model + Color grouped */}
            <div className="vehicle-row vehicle-row--details">
              <div className="vehicle-field">
                <label>Model</label>
                <input
                  type="text"
                  className="form-input"
                  value={localData.model}
                  disabled={isFieldsDisabled}
                  onChange={(e) => handleFieldChange('model', e.target.value)}
                  placeholder="Model"
                />
              </div>
              <div className="vehicle-field">
                <label>Color</label>
                <input
                  type="text"
                  className="form-input"
                  value={localData.color}
                  disabled={isFieldsDisabled}
                  onChange={(e) => handleFieldChange('color', e.target.value)}
                  placeholder="Color"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="vehicle-actions">
            {isLinked ? (
              <>
                <span className="linked-badge">
                  <i className="fas fa-link"></i> Linked to Vehicle Record
                </span>
                <button
                  type="button"
                  className="btn btn-link-action"
                  onClick={handleShowLinkedVehicle}
                  title="View linked vehicle"
                >
                  View
                </button>
                <button
                  type="button"
                  className="btn btn-link-action"
                  onClick={handleUnlinkVehicle}
                  disabled={isReadOnly || isLocked}
                  title="Unlink from vehicle record"
                >
                  Unlink
                </button>
              </>
            ) : (
              <>
                <div className="vehicle-actions-left">
                  <button
                    type="button"
                    className="btn btn-link-action"
                    onClick={handleLinkVehicle}
                    disabled={isReadOnly || isLocked}
                    title="Link to existing vehicle"
                  >
                    <i className="fas fa-link"></i> Link
                  </button>
                  <button
                    type="button"
                    className="btn btn-link-action btn-create"
                    onClick={handleCreateVehicle}
                    disabled={!canCreate}
                    title="Create new vehicle record"
                  >
                    <i className="fas fa-plus"></i> Create
                  </button>
                </div>
                <div className="vehicle-actions-right">
                  <button
                    type="button"
                    className="btn btn-link-action btn-clear"
                    onClick={handleOpenClearConfirm}
                    disabled={isReadOnly || isLocked}
                    title="Clear all vehicle data"
                  >
                    <i className="fas fa-times"></i> Clear
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <ConfirmModal
        open={showClearConfirm}
        title="Clear Vehicle Data?"
        confirmText="Clear"
        cancelText="Cancel"
        autoFocus="cancel"
        onConfirm={handleConfirmClear}
        onCancel={handleCancelClear}
      >
        <p>This will clear all entered vehicle information. This action cannot be undone.</p>
      </ConfirmModal>
    </div>
  );
}

(window as any).CitationVehicleReact = CitationVehicle;
